import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from './useSubscription';
import { trackConversion, trackPurchase } from '@/lib/analytics';
import { PLANS_BY_TIER, SUPPORT_EMAIL, hasPaidSubscription, planPrice, planTierLabel } from '@/lib/plans';
import { numberLocale } from '@/lib/language';
import { getCurrency } from '@/lib/checkout';
import {
  CHECKOUT_AWAITING_KEY,
  CHECKOUT_AWAITING_TTL_MS,
  clearCheckoutAwaiting,
  isCheckoutAwaiting,
  markCheckoutAwaiting,
} from '@/lib/checkoutAwaiting';

export const CHECKOUT_PENDING_TOAST_ID = 'checkout-pending';
/** So lange wartet die Seite auf den Webhook, bevor sie ehrlich "dauert" sagt. */
export const CHECKOUT_CONFIRM_TIMEOUT_MS = 90_000;
// Sitzungsmarke "Kauf wartet auf Bestaetigung": Helfer in lib/checkoutAwaiting.ts,
// damit AuthContext sie beim Abmelden raeumen kann (NA1 Task 4b). Weiter exportiert,
// weil Tests und Leser die Konstanten von hier importieren.
export { CHECKOUT_AWAITING_KEY, CHECKOUT_AWAITING_TTL_MS };

/** Marke je Stripe-Abo: der Kauf geht nur einmal nach GA4, auch ueber Tabs und Reloads. */
const TRACKED_KEY_PREFIX = 'checkout_tracked:';
/** Alte Marke (nur Sitzung), weiter gelesen, damit ein laufender Tab nicht doppelt meldet. */
const LEGACY_SESSION_KEY = 'checkout_tracked';

function hasParam(name: string): boolean {
  return new URLSearchParams(window.location.search).has(name);
}

function stripParam(name: string): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(name)) return;
  url.searchParams.delete(name);
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);
}

/**
 * Setzt die Kaufmarke und meldet, ob dieser Aufruf sie gesetzt hat. Ist der Speicher
 * gesperrt (privates Fenster), wird trotzdem gemeldet: eine doppelte Meldung nach einem
 * Reload ist billiger als ein verlorener Kauf.
 */
function claimPurchaseTracking(transactionId: string): boolean {
  const key = TRACKED_KEY_PREFIX + transactionId;
  try {
    if (localStorage.getItem(key) || sessionStorage.getItem(LEGACY_SESSION_KEY)) return false;
    localStorage.setItem(key, '1');
  } catch {
    // Speicher nicht verfuegbar: Meldung trotzdem senden (siehe oben).
  }
  return true;
}

/**
 * Rueckkehr aus dem Stripe-Checkout (UX-Audit G4-03):
 *   - `?success=true` und Abo aktiv: Erfolgs-Toast mit Tarif und Kapazitaet,
 *     GA4-Kauf genau einmal je Abo, Parameter weg.
 *   - `?success=true`, Webhook noch nicht da: "Zahlung wird bestaetigt", nach
 *     90 s ein ehrlicher Hinweis. Der Parameter wird sofort verbraucht (Ruling
 *     N2, loest R12 im Wortlaut ab); das Warten lebt in einem Ref und in der
 *     Sitzungsmarke `checkout_awaiting` (6 h ab der ersten Rueckkehr), damit ein
 *     spaeter Webhook den Kauf auch nach einem Reload noch genau einmal meldet.
 *   - `?canceled=true`: neutraler Hinweis mit Weg zu den Tarifen.
 *   - Wechselt das angemeldete Konto waehrend des Wartens, endet das Warten
 *     (Ruling N4); das Abo des neuen Kontos wird nicht als Kauf gemeldet.
 * Vorher feuerte nur das Tracking, der Nutzer sah nichts.
 */
export function useCheckoutSuccess(): void {
  const { t, i18n } = useTranslation('checkout');
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const lastUserIdRef = useRef<string | null>(userId);
  const { subscription } = useSubscription();
  const doneRef = useRef(false);
  const pendingShownRef = useRef(false);
  const canceledHandledRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  // G4-03 Rest (Ruling N2): "Kauf wartet auf Bestaetigung" lebt hier und in der
  // Sitzungsmarke, nicht mehr in der URL.
  const awaitingRef = useRef(false);
  // Die EINE Einordnung aus lib/plans.ts (lebendes Stripe-Abo, bezahltes Band),
  // dieselbe wie auf allen Kauf-Flaechen und am Server (hasLiveSubscription).
  const activated = hasPaidSubscription(subscription);

  // Verlaesst der Nutzer die Seite waehrend des Wartens, darf der Lade-Toast nicht
  // endlos auf der naechsten Seite weiterdrehen.
  useEffect(() => () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
      toast.dismiss(CHECKOUT_PENDING_TOAST_ID);
    }
  }, []);

  // Abbruch
  useEffect(() => {
    if (canceledHandledRef.current || !hasParam('canceled')) return;
    canceledHandledRef.current = true;
    stripParam('canceled');
    toast.info(t('canceled.title'), {
      action: { label: t('canceled.cta'), onClick: () => navigate('/pricing') },
    });
  }, [t, navigate]);

  // Ruling N4: das Warten gehoert dem Konto, das zum Checkout ging. Wechselt
  // das Konto (oder meldet es sich ab), endet es hier, auch wenn der Hook
  // gemountet bleibt. Die erste Aufloesung (null -> id) ist KEIN Wechsel:
  // sonst verloere die Rueckkehr aus dem Checkout ihr Warten, bevor der
  // Nutzer geladen ist.
  //
  // Steht bewusst VOR dem Effekt "Erfolg": Effekte laufen in
  // Deklarationsreihenfolge, und wechseln Konto und Abo im selben Commit, muss
  // das Zuruecksetzen vor der Pruefung laufen, sonst meldete "Erfolg" das Abo
  // des neuen Kontos als Kauf (trackPurchase, Google-Ads-Import).
  // clearCheckoutAwaiting() ist Absicht, obwohl AuthContext die Marke schon
  // vor setUser raeumt: der Hook haengt nicht von der Reihenfolge in einer
  // anderen Datei ab.
  useEffect(() => {
    const previous = lastUserIdRef.current;
    lastUserIdRef.current = userId;
    if (previous === null || previous === userId) return;
    const wasWaiting = awaitingRef.current || timerRef.current !== null;
    awaitingRef.current = false;
    pendingShownRef.current = false;
    clearCheckoutAwaiting();
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (wasWaiting) toast.dismiss(CHECKOUT_PENDING_TOAST_ID);
  }, [userId]);

  // Erfolg
  useEffect(() => {
    if (doneRef.current) return;
    if (!hasParam('success') && !awaitingRef.current && !isCheckoutAwaiting()) return;
    if (!activated || !subscription) return;
    doneRef.current = true;
    awaitingRef.current = false;
    clearCheckoutAwaiting();
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const plan = PLANS_BY_TIER[subscription.plan_tier];
    const limit = subscription.product_limit ?? plan?.productLimit ?? 0;
    toast.success(t('success.title', { plan: planTierLabel(subscription.plan_tier, i18n?.language, t) }), {
      id: CHECKOUT_PENDING_TOAST_ID,
      description: t('success.description', { limit: limit.toLocaleString(numberLocale(i18n?.language)) }),
      duration: 10_000,
    });

    const transactionId = subscription.stripe_subscription_id || subscription.id;
    if (claimPurchaseTracking(transactionId)) {
      const currency = getCurrency();
      const interval = subscription.billing_interval || 'monthly';
      // `planPrice` deckt auch die Band-Tiers ab, deren Jahres-Strings Tausendertrenner tragen.
      const value = plan ? planPrice(plan, currency, interval === 'yearly' ? 'yearly' : 'monthly') : 0;
      trackPurchase({
        transactionId,
        planTier: subscription.plan_tier,
        planName: plan?.name || subscription.plan_tier,
        value,
        currency: currency === 'eur' ? 'EUR' : 'USD',
        billingInterval: interval,
      });
      trackConversion('subscription_activated', {
        plan: subscription.plan_tier,
        billing_interval: interval,
      });
    }

    stripParam('success');
  }, [activated, subscription, t, i18n?.language]);

  // Warten auf den Webhook
  useEffect(() => {
    if (doneRef.current || pendingShownRef.current) return;
    if (!hasParam('success') && !isCheckoutAwaiting()) return;
    if (activated) return;
    pendingShownRef.current = true;
    awaitingRef.current = true;
    markCheckoutAwaiting();
    // Der Parameter ist verbraucht, sobald das Warten sichtbar ist. Der Zustand
    // lebt ab hier in `awaitingRef` und der Sitzungsmarke (Ruling N2).
    stripParam('success');
    toast.loading(t('success.pending'), { id: CHECKOUT_PENDING_TOAST_ID });
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      if (doneRef.current) return;
      toast.info(t('success.slow', { email: SUPPORT_EMAIL }), { id: CHECKOUT_PENDING_TOAST_ID, duration: 12_000 });
    }, CHECKOUT_CONFIRM_TIMEOUT_MS);
  }, [activated, t]);
}
